import { useGovernanceVote, useValidatorSnapshot } from '@/hooks/useGovernanceVote';
import { calculateVoteResults, getVoteStatus, getMockScenarioInfo } from '@/utils/voteCalculations';
import { VoteHeader } from '@/components/vote/VoteHeader';
import { VoteTally } from '@/components/vote/VoteTally';
import { VoteTimeline } from '@/components/vote/VoteTimeline';
import { ValidatorVoteList } from '@/components/vote/ValidatorVoteList';
import { LoadingState } from '@/components/shared/LoadingState';
import { ErrorState } from '@/components/shared/ErrorState';
import { Badge } from '@/components/ui/badge';
import { useRef } from 'react';

export function VoteView() {
    const { data: vote, isLoading: voteLoading, error: voteError } = useGovernanceVote();
    const { data: snapshot, isLoading: snapshotLoading, error: snapshotError } = useValidatorSnapshot();
    const validatorListRef = useRef<HTMLDivElement>(null);

    // Get mock scenario info for development
    const mockInfo = getMockScenarioInfo();
    const isDevelopment = import.meta.env.DEV;
    const isUsingMock = mockInfo.scenario.startsWith('mock');

    const scrollToValidatorList = () => {
        validatorListRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    };

    if (voteLoading || snapshotLoading) {
        return (
            <div className='flex-1 overflow-y-auto overflow-x-hidden flex max-w-full 
        [&::-webkit-scrollbar-track]:bg-gray-100
        [&::-webkit-scrollbar-thumb]:bg-gray-300
        dark:[&::-webkit-scrollbar-track]:bg-neutral-700
        dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500'
            >
                <div className='flex flex-col gap-1 w-full h-full p-4'>
                    <LoadingState />
                </div>
            </div>
        );
    }

    if (voteError || snapshotError) {
        return (
            <div className='flex-1 overflow-y-auto overflow-x-hidden flex max-w-full 
        [&::-webkit-scrollbar-track]:bg-gray-100
        [&::-webkit-scrollbar-thumb]:bg-gray-300
        dark:[&::-webkit-scrollbar-track]:bg-neutral-700
        dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500'
            >
                <div className='flex flex-col gap-1 w-full h-full p-4'>
                    <ErrorState
                        title="Failed to load vote data"
                        description="There was an error loading the governance vote information. Please try again."
                    />
                </div>
            </div>
        );
    }

    if (!vote || !snapshot) {
        return (
            <div className='flex-1 overflow-y-auto overflow-x-hidden flex max-w-full 
        [&::-webkit-scrollbar-track]:bg-gray-100
        [&::-webkit-scrollbar-thumb]:bg-gray-300
        dark:[&::-webkit-scrollbar-track]:bg-neutral-700
        dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500'
            >
                <div className='flex flex-col gap-1 w-full h-full p-4'>
                    <div className="text-center py-12">
                        <h2 className="text-2xl font-bold mb-4">No Active Vote</h2>
                        <p className="text-muted-foreground">
                            There is currently no active governance vote. Check back later for upcoming votes.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const results = calculateVoteResults(vote.votes, snapshot);
    const voteStatus = getVoteStatus(vote);

    return (
        <div className='flex-1 overflow-y-auto overflow-x-hidden flex max-w-full 
      [&::-webkit-scrollbar-track]:bg-gray-100
      [&::-webkit-scrollbar-thumb]:bg-gray-300
      dark:[&::-webkit-scrollbar-track]:bg-neutral-700
      dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500'
        >
            <div className='flex flex-col gap-2 w-full h-full p-2 md:px-4'>
                {/* Development Mock Scenario Indicator */}

                {/* Unified grid for VoteHeader, Sidebar, and ValidatorVoteList */}
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                    {/* VoteHeader - 2/3 width on large screens, full width on smaller */}
                    <div className="xl:col-span-2"> {/* Implicitly xl:row-start-1 */}
                        <VoteHeader vote={vote} voteStatus={voteStatus} />
                    </div>

                    {/* Right column - VoteTimeline and VoteTally stacked */}
                    {/* This group appears after VoteHeader and before ValidatorVoteList on mobile (source order) */}
                    {/* On XL, it's in the 3rd column, spanning alongside Header and List */}
                    <div className="flex flex-col gap-4 xl:col-start-3 xl:row-start-1 xl:row-span-2">
                        <VoteTimeline
                            start_timestamp_ms={vote.start_timestamp_ms}
                            minimum_duration_timestamp_ms={vote.minimum_duration_timestamp_ms}
                            end_timestamp_ms={vote.end_timestamp_ms}
                            voteResult={vote.result}
                        />
                        <VoteTally
                            results={results}
                            voteStatus={voteStatus}
                            vote={vote}
                            onScrollToDetails={scrollToValidatorList}
                        />
                    </div>

                    {/* ValidatorVoteList - appears below VoteHeader on XL, last on mobile */}
                    <div className="xl:col-span-2 xl:row-start-2" ref={validatorListRef}>
                        <ValidatorVoteList
                            votes={vote.votes}
                            snapshot={snapshot}
                            results={results}
                            voteStatus={voteStatus}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
} 